import React, { useEffect, useState } from 'react';

import { useRive } from '@rive-app/react-canvas';
import usePathname from 'app-migration/hooks/usePathname';
import { observer } from 'mobx-react';
import { createUseStyles } from 'react-jss';

import { getDepositStatement } from 'constants/statements';
import Button from 'DesignSystem2/Button/Button';
import Text from 'DesignSystem2/Text';
import { Spacing } from 'DesignSystem2/Theme';
import Padding from 'DesignSystem2/Theme/Padding';
import Palette from 'DesignSystem2/Theme/Palette';
import { useStores } from 'hooks/use-stores';
import TransfersLoading from 'react_pages/TransfersLoading';
import { FundMethods } from 'stores/ui/registration/registration';
import { fireImpactDeposit } from 'utils/analytics/providers/impact';
import { fireDepositedPostBacks, fireReferralDepositPostBack } from 'utils/analytics/providers/predictionNews';
import { toMoneyWithCentsIfCents } from 'utils/caseing';

// eslint-disable-next-line  @typescript-eslint/no-explicit-any
// declare const tatari: any;

const useStyles = createUseStyles({
    row: {
        display: 'flex',
        gap: Spacing(1),
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    column: {
        display: 'flex',
        flexDirection: 'column',
    },
    centered: {
        alignItems: 'center',
        textAlign: 'center',
    },
});

interface DepositReviewStepProps {
    amount: number;
    bankId: string;
    cardId: string;
    loading: boolean;
    submitDeposit: () => void;
    onEndDeposit: () => void;
    edit: () => void;
    tryAgain: () => void;
    goToOrderReview?: () => void;
}

const DepositReviewStep = ({
    amount,
    bankId,
    cardId,
    loading,
    submitDeposit,
    onEndDeposit,
    edit,
    tryAgain,
    goToOrderReview,
}: DepositReviewStepProps): JSX.Element => {
    const classes = useStyles();
    const { uiStore, traderStore } = useStores();
    const userStore = traderStore.user;
    const isDarkMode = uiStore.colorPalette.currentColorPalette === 'dark';

    const { rive, RiveComponent } = useRive({
        src: '/animations/animations.riv',
        autoplay: true,
        artboard: isDarkMode ? 'bill-dark' : 'bill-light',
        animations: ['appear'],
    });

    const pathname = usePathname();

    const registrationStore = uiStore.registration;
    const paymentStore = traderStore.payments;
    const dialog = uiStore.depositDialog;
    const paymentsStore = traderStore.payments;

    const success = dialog.step === 'initiated';
    const fail = dialog.step === 'failed';
    const errorTitle = dialog.errorTitle || '';
    const errorMessage = dialog.errorMessage || '';
    const errorCode = dialog.errorCode || '';
    const bankStore = traderStore.bank;
    const bankAccount = bankStore.resolveAccount(bankId);

    const [loadingDebitDeposit, setLoadingDebitDeposit] = useState<boolean>(false);

    useEffect(() => {
        // Only record the metric if we're in the sign-up flow
        const isSignUp = pathname.includes('/sign-up');
        if (isSignUp) {
            userStore.metrics.recordSignUpMetric({
                step: 'reviewDeposit',
            });
        } else {
            userStore.metrics.recordDepositMetric({
                step: 'openReviewDeposit',
            });
        }
    }, []);

    // Deposit result
    const fundMethod = uiStore.registration.fundMethod;
    const isBankFundMethod = fundMethod === FundMethods.BANK;
    const isDebitCardFundMethod = fundMethod === FundMethods.DEBIT_CARD;
    const fullAmount = amount > 0 ? amount : dialog.amount; // If user is redirected from debit card security check, amount is set on dialog amount on RouterWithLayout.
    const fee = dialog.fee; // If user is redirected from debit card security check, fee is set on dialog amount on RouterWithLayout.
    let depositReady = false;
    let transferFrom: string | undefined = undefined;
    let feeText: string | undefined = undefined;

    // Bank deposit flow
    if (isBankFundMethod) {
        depositReady = true; // Bank deposits are always ready to be submitted
        transferFrom = bankAccount?.name;
    }
    // Card deposit flow
    else if (isDebitCardFundMethod) {
        const paymentStatus = paymentStore.paymentStatus;
        const debitCard = paymentsStore.debitCards.find((card) => card.id === cardId);
        depositReady = paymentStatus === 'success';
        transferFrom = debitCard ? `${debitCard.type} ${debitCard.name}` : 'Debit card';
        feeText = !!fee ? `${toMoneyWithCentsIfCents(fee)} processing fee` : undefined;
    }

    const onSuccessDone = () => {
        onEndDeposit();
    };

    const onContinueWithSavedCard = (): void => {
        userStore.metrics.recordDepositMetric({
            step: 'submitCardInstrumentInit',
        });
        setLoadingDebitDeposit(true);
        paymentsStore.savedDebitCardDeposit(
            traderStore.user.getUserId,
            amount,
            fee,
            cardId,
            pathname,
            (response) => {
                // Check for immediate failure
                if (response?.failure_title || response?.failure_description) {
                    dialog.setErrorTitle(response.failure_title || 'Payment failed');
                    dialog.setErrorMessage(response.failure_description || 'Please try again');
                    dialog.setStep('failed');
                    dialog.setFee(0);
                    setLoadingDebitDeposit(false);
                    return;
                }

                // If 3DS is required, redirect
                if (!!response['3DSlink']) {
                    userStore.metrics.recordDepositMetric({
                        step: 'cardChallengeRequested',
                    });
                    userStore.metrics.recordDepositMetric({
                        step: 'cardChallengeRequestedInit',
                    });
                    window.location.href = response['3DSlink'];
                    return;
                }

                // Payment succeeded without 3DS
                if (response?.payment_success === true) {
                    uiStore.heapTrack('DepositWithSavedDebitCardStart', {
                        userId: traderStore.user.getUserId,
                        amount: amount,
                        fee: fee,
                        cardId: cardId,
                    });
                    dialog.setFee(0);
                    dialog.setStep('initiated');
                    userStore.metrics.recordDepositMetric({
                        step: 'submitCardInstrumentSuccess',
                    });
                    setLoadingDebitDeposit(false);
                    fireDepositedPostBacks(bankStore.depositsCount, amount, uiStore.getPredictionNewsUserId());
                    // Also fire referral postback if user has a referral code
                    const referralCode = traderStore.user.referrals?.referralInfo?.referral_code;
                    if (referralCode) {
                        fireReferralDepositPostBack(referralCode);
                    }

                    // Track saved debit card deposit with Impact.com
                    fireImpactDeposit(
                        traderStore.user.getUserId,
                        amount,
                        response.payment_id,
                        undefined, // promo code if available
                    ).catch((err) => {
                        console.error('Failed to track Impact saved debit card deposit:', err);
                    });
                }
            },
            (message, details, code) => {
                userStore.metrics.recordDepositMetric({
                    step: 'submitCardInstrumentError',
                });
                dialog.setErrorTitle(message);
                dialog.setErrorMessage(details);
                dialog.setErrorCode(code);
                dialog.setStep('failed');
                dialog.setFee(0);
                setLoadingDebitDeposit(false);
            },
        );
    };

    const isDebitCard3DSFailed = errorCode === 'debit_card_payment_failed_3ds';

    const fullAmountLabel = toMoneyWithCentsIfCents(fullAmount);
    const detaieldAmountLabel = !!fee ? `${toMoneyWithCentsIfCents(fullAmount)}` : toMoneyWithCentsIfCents(fullAmount);

    const handleSetText = async () => {
        const { firstName, lastName } = registrationStore;

        if (isDarkMode) {
            rive?.reset({ artboard: 'instant-bill-dark' });
        } else {
            rive?.reset({ artboard: 'instant-bill-light' });
        }

        rive?.setTextRunValue('Method', isDebitCardFundMethod ? 'debit card' : 'bank transfer');
        rive?.setTextRunValue('Deposit', fullAmountLabel);
        rive?.setTextRunValue('Fee', !!fee ? `${toMoneyWithCentsIfCents(fee)}` : '');
        rive?.setTextRunValue('Available', fullAmountLabel);
        rive?.setTextRunValue('Name', `${firstName} ${lastName}`);

        rive?.play();
    };

    useEffect(() => {
        registrationStore.load();
        if (!rive || !registrationStore.loaded) return;

        handleSetText();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rive, registrationStore]);

    return (
        <div className={'flex flex-col gap-3 animate-fade-in'}>
            {loading || dialog.loading ? (
                <TransfersLoading amount={fullAmount} />
            ) : fail ? (
                <div className={classes.centered}>
                    {!!errorTitle && (
                        <>
                            {typeof errorTitle !== 'string' ? (
                                errorTitle
                            ) : (
                                <Text.Heading.x15>{errorTitle}</Text.Heading.x15>
                            )}
                            <Padding spacing={3} />
                        </>
                    )}
                    {!!errorMessage && (
                        <>
                            {typeof errorMessage !== 'string' ? (
                                errorMessage
                            ) : (
                                <Text.Body.x30>
                                    {errorMessage.split('\n').map((line, index) => (
                                        <p key={index} dangerouslySetInnerHTML={{ __html: line }} />
                                    ))}
                                </Text.Body.x30>
                            )}
                            <Padding spacing={3} />
                        </>
                    )}
                    {errorCode === 'document_verification_required' ? (
                        <Button variant={'brand'} href={'/sign-up/identity'} text={'Verify documents'} fullWidth />
                    ) : errorCode === 'reduce_order_size' ? (
                        <Button variant={'brand'} onClick={goToOrderReview} text={dialog.errorContinueText} fullWidth />
                    ) : isDebitCard3DSFailed ? (
                        <Button
                            variant={'brand'}
                            onClick={() => {
                                dialog.setInitialStep();
                                uiStore.registration.setFundMethod(undefined);
                            }}
                            text={'Okay'}
                            fullWidth
                        />
                    ) : (
                        <Button variant={'brand'} onClick={tryAgain} text={dialog.errorContinueText} fullWidth />
                    )}
                    {!isDebitCard3DSFailed && (
                        <>
                            <Padding spacing={2} />
                            <Button
                                variant={'brand'}
                                rank={'tertiary'}
                                onClick={onEndDeposit}
                                text={'Close'}
                                fullWidth
                            />
                        </>
                    )}
                </div>
            ) : success ? (
                <>
                    <div className={'p-0.5'}>
                        <RiveComponent className={'w-full h-[400px] flex items-center justify-center'} />
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: Spacing(1),
                        }}
                    >
                        {isBankFundMethod && !depositReady && dialog.step !== 'kyc' && (
                            <Text.Body.x30 color={Palette.Green.x10}>{'3-5 business days transfer time'}</Text.Body.x30>
                        )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: Spacing(2) }}>
                        <Button variant={'brand'} onClick={onSuccessDone} text={'Done'} loading={loading} />
                    </div>
                </>
            ) : (
                <>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: Spacing(1),
                            marginTop: Spacing(1),
                        }}
                    >
                        {!!transferFrom && !isDebitCardFundMethod && (
                            <Text.Body.x30 color={Palette.Text.x10}>{`From ${transferFrom}${
                                !!feeText ? ` · ${feeText}` : ''
                            }`}</Text.Body.x30>
                        )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: Spacing(2) }}>
                        {isBankFundMethod && (
                            <Text.Body.x20 color={Palette.Text.x20}>{getDepositStatement()}</Text.Body.x20>
                        )}
                        <Button
                            variant={'brand'}
                            onClick={isBankFundMethod ? submitDeposit : onContinueWithSavedCard}
                            text={`Deposit ${detaieldAmountLabel}`}
                            loading={loading || loadingDebitDeposit}
                        />
                        <Button
                            variant={'brand'}
                            rank={'secondary'}
                            onClick={edit}
                            text={'Edit'}
                            disabled={loading || loadingDebitDeposit}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default observer(DepositReviewStep);
